import { Button, Grid, GridItem, Icon, Modal, Tooltip, Typography } from "@drone-js/core";
import { HStack, VStack } from "apps/commons/components/Stack";
import { replaceCms } from "apps/commons/functions/replaceCms";
import type { CountryDropdownItem, ProfileData } from "libs/codegen/property-onboarding";
import { type FunctionComponent, useState } from "react";
import { type FieldPath, useFormContext } from "react-hook-form";
import { DropdownField } from "../form/DropdownField";
import { TextField } from "../form/TextField";
import { useStaticDataAtomValue } from "../staticDataAtom";
import { BirthdayField } from "./BirthdayField";
import { CityStateDropdown } from "./CityStateDropdown";

type ValidPath = FieldPath<ProfileData>;

interface Props {
    fieldPrefix: string;
    multiplePrefix?: string;
    multiple: boolean;
    index?: number;
    onRemove?: () => void;
    copiable?: boolean;
    disableStateCityFields?: boolean;
    removeModalTitle?: string;
    removeModalDescription?: string;
    prefixDataElementName?: string;
    secondPrefixDataElementName?: string;
}

export const ComplianceDetailsFields: FunctionComponent<Props> = ({
    fieldPrefix,
    multiple,
    multiplePrefix,
    index,
    onRemove,
    disableStateCityFields,
    removeModalTitle,
    removeModalDescription,
    prefixDataElementName,
    secondPrefixDataElementName,
}) => {
    const {
        cmsData: {
            profile: {
                personalInformation,
                firstName,
                firstNameRequired,
                lastName,
                lastNameRequired,
                nationality,
                nationalityRequired,
                residenceInformation,
                country,
                countryRequired,
                residenceAddress,
                residenceZipCode,
                residenceInformationTooltip,
                cancel,
                remove,
            },
        },
    } = useStaticDataAtomValue();
    const { countriesList } = useStaticDataAtomValue();
    const [removeModal, setRemoveModal] = useState(false);
    const { setValue } = useFormContext();

    return (
        <VStack gap="16">
            {multiple && (
                <HStack alignItems="center" justifyContent="space-between">
                    <Typography variant="body-default-strong">{replaceCms(multiplePrefix, index + 1)}</Typography>
                    <Button
                        size="s"
                        variant="icon"
                        hierarchy="tertiary"
                        leadingIcon="fill.symbol.bin"
                        onClick={() => setRemoveModal(true)}
                        data-element-name={secondPrefixDataElementName ? "remove-" + secondPrefixDataElementName : ""}
                    />
                </HStack>
            )}
            <VStack gap="24" backgroundColor="base.inset" p="16" borderRadius="8">
                <VStack gap="16">
                    <Typography variant="body-s-strong">{personalInformation}</Typography>
                    <Grid spacing="16">
                        <GridItem sm={12} md={6}>
                            <TextField<ProfileData>
                                label={firstName}
                                field={`${fieldPrefix}.firstName` as ValidPath}
                                rules={{
                                    required: firstNameRequired,
                                }}
                                dataElementName={prefixDataElementName ? prefixDataElementName + "-first-name" : ""}
                            />
                        </GridItem>
                        <GridItem sm={12} md={6}>
                            <TextField<ProfileData>
                                label={lastName}
                                field={`${fieldPrefix}.lastName` as ValidPath}
                                rules={{
                                    required: lastNameRequired,
                                }}
                                dataElementName={prefixDataElementName ? prefixDataElementName + "-last-name" : ""}
                            />
                        </GridItem>
                    </Grid>
                    <DropdownField<ProfileData, CountryDropdownItem>
                        label={nationality}
                        field={`${fieldPrefix}.nationality` as ValidPath}
                        items={countriesList}
                        identifyBy={fieldPrefix === "hostDetails" ? "id" : "iso2"}
                        rules={{
                            required: nationalityRequired,
                            validate: {
                                notEmpty: (v) => {
                                    if (fieldPrefix === "hostDetails") {
                                        return (
                                            (v !== null && (v as CountryDropdownItem).id !== 0) || nationalityRequired
                                        );
                                    }
                                    return (
                                        (v !== null && (v as CountryDropdownItem).iso2 !== "") || nationalityRequired
                                    );
                                },
                            },
                        }}
                        dataElementName={prefixDataElementName ? prefixDataElementName + "-nationality" : ""}
                    />
                    <BirthdayField
                        fieldPrefix={fieldPrefix}
                        dataElementName={prefixDataElementName ? prefixDataElementName + "-birth-date" : ""}
                    />
                </VStack>
                <VStack gap="16">
                    <HStack gap="8" alignItems="center">
                        <Typography variant="body-s-strong">{residenceInformation}</Typography>
                        <Tooltip
                            content={residenceInformationTooltip}
                            pointerPosition="above"
                            data-element-name={
                                prefixDataElementName ? prefixDataElementName + "-residence-info-tooltip" : ""
                            }
                        >
                            <Icon id="fill.symbol.circle-info" size="16" />
                        </Tooltip>
                    </HStack>
                    <DropdownField<ProfileData, CountryDropdownItem>
                        label={country}
                        field={`${fieldPrefix}.country` as ValidPath}
                        items={countriesList}
                        identifyBy={fieldPrefix === "hostDetails" ? "id" : "iso2"}
                        rules={{
                            required: countryRequired,
                            validate: {
                                notEmpty: (v) => {
                                    if (fieldPrefix === "hostDetails") {
                                        return (v !== null && (v as CountryDropdownItem).id !== 0) || countryRequired;
                                    }
                                    return (v !== null && (v as CountryDropdownItem).iso2 !== "") || countryRequired;
                                },
                            },
                        }}
                        onChange={() => {
                            // when country changed, reset value for city and state
                            setValue(`${fieldPrefix}.state`, undefined);
                            setValue(`${fieldPrefix}.city`, undefined);
                        }}
                        dataElementName={prefixDataElementName ? prefixDataElementName + "-residence-info-country" : ""}
                    />
                    {!disableStateCityFields && (
                        <CityStateDropdown fieldPrefix={fieldPrefix} dataElementName={prefixDataElementName} />
                    )}
                    <TextField<ProfileData>
                        label={residenceAddress}
                        field={`${fieldPrefix}.address` as ValidPath}
                        dataElementName={prefixDataElementName ? prefixDataElementName + "-residence-info-address" : ""}
                    />
                    <TextField<ProfileData>
                        label={residenceZipCode}
                        field={`${fieldPrefix}.postalCode` as ValidPath}
                        dataElementName={
                            prefixDataElementName ? prefixDataElementName + "-residence-info-zip-code" : ""
                        }
                    />
                </VStack>
            </VStack>
            {multiple && (
                <Modal
                    title={replaceCms(removeModalTitle, index + 1)}
                    visible={removeModal}
                    onClose={() => setRemoveModal(false)}
                    actions={[
                        {
                            actionType: "button",
                            hierarchy: "primary",
                            variant: "label",
                            label: remove,
                            onClick: onRemove,
                            "data-element-name": secondPrefixDataElementName
                                ? "confirm-remove-" + secondPrefixDataElementName
                                : "",
                        },
                        {
                            actionType: "textLink",
                            hierarchy: "secondary",
                            label: cancel,
                            onClick: () => setRemoveModal(false),
                        },
                    ]}
                    zIndex={1060}
                >
                    <Typography variant="body-s">{removeModalDescription}</Typography>
                </Modal>
            )}
        </VStack>
    );
};
